import React from "react";

import classNames from "classnames";

import { StyledBadge } from "./Styles";

type Position = "top-left" | "top-right" | "bottom-right" | "bottom-left";
type Shape = "circle" | "pill";

export interface BadgeProps {
  position?: Position;
  dimension?: number;
  className?: string;
  backgroundColor?: string;
  textColor?: string;
  value?: number;
  cap?: number;
  shape?: Shape;
  shadows?: boolean;
  isStandalone?: boolean;
}

const getLabel = (value?: number, cap?: number) => {
  if (!value) {
    return null;
  }

  return cap && value > cap ? `${cap}+` : value;
};

/**
 * @component Badge
 * @description
 * A small pill that sits on the corner of an avatar or an icon, or stands in a
 * row on its own. It shows a count, or nothing at all when it marks a state.
 *
 * @prop {position} - dictates the position of a badge.
 * @prop {dimension} - controls the height of a badge in pixels. The width grows with the content.
 * @prop {shape} - "pill" adds horizontal padding so the badge reads as long.
 * @prop {cap} - the highest number shown. Above it the badge renders `{cap}+`.
 * @prop {backgroundColor} - background-color of a badge.
 * @prop {textColor} - color of the number.
 * @prop {isStandalone} - takes the badge out of absolute positioning.
 *
 * @example
 * <IconButton variant="text gray" size="big">
 *   <BellOffIcon />
 *   <Badge position="top-right" backgroundColor="var(--red-alert)" dimension={16} value={7} cap={99} />
 * </IconButton>
 *
 * @see
 * https://system.activecollab.com/?path=/story/components-indicators-badge--badge
 */
export const Badge = ({
  dimension = 8,
  className,
  position = "bottom-left",
  backgroundColor,
  textColor,
  value,
  cap,
  shape = "circle",
  shadows = false,
  isStandalone = false,
}: BadgeProps) => {
  const label = getLabel(value, cap);

  return (
    <StyledBadge
      className={classNames("c-badge", className)}
      dimension={dimension}
      position={position}
      shape={shape}
      shadows={shadows}
      hasLabel={label !== null}
      isStandalone={isStandalone}
      backgroundColor={backgroundColor}
      textColor={textColor}
      role="status"
    >
      {label}
    </StyledBadge>
  );
};
